import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useApiFetch } from '../hooks/useApiFetch';
import { EvalSetFilter } from './EvalSetFilter';
import {
  DEFAULT_MODE,
  DEFAULT_SORT,
  type ContentKind,
  type IndexStatus,
  type SearchMethod,
  type SearchMode,
  type SearchResponse,
  type SearchResult,
  type SearchSort,
  type TooBroad,
} from '../types/search';
import { formatApproximateCount } from '../utils/formatters';
import { type SampleViewAnchor } from '../utils/url';
import { useViewers } from '../hooks/useViewers';
import { viewerLinkTarget, type ViewerTarget } from '../utils/viewers';
import { OpenInViewerMenu } from './OpenInMenu';
import { PageHeader } from './PageHeader';
import { SearchSnippet } from './SearchSnippet';

const DEBOUNCE_MS = 300;
const LIMIT = 50;
// Automatic paging stops after this long until the user continues it.
const AUTO_SEARCH_MS = 10 * 60 * 1000;

const SCAN_NOTICE =
  'The index could not answer this search in time. Results come from a scan, newest first, without relevance ranking.';

interface SearchRequest {
  query: string;
  evalSetId: string;
  sort: SearchSort;
  mode: SearchMode;
  matchCase: boolean;
  key: string;
  cursor?: string | null;
  // The cursor came from a scan page.
  scanCursor?: boolean;
}

interface SearchState {
  key: string;
  results: SearchResult[];
  nextCursor: string | null;
  // Creation time of the oldest visible record a scan has checked.
  searchedThrough: string | null;
  indexStatus: IndexStatus | null;
  method: SearchMethod | null;
  tooBroad: TooBroad | null;
  recovery: 'retry' | 'restart' | null;
  loading: boolean;
  error: Error | null;
  searched: boolean;
}

const EMPTY_SEARCH: SearchState = {
  key: '',
  results: [],
  nextCursor: null,
  searchedThrough: null,
  indexStatus: null,
  method: null,
  tooBroad: null,
  recovery: null,
  loading: false,
  error: null,
  searched: false,
};

// Mirrors meta_server filename derivation exactly: `location.split(f"{eval_set_id}/")[-1]`
// — take the segment after the LAST occurrence of `${evalSetId}/`, falling back to
// the whole location if the marker isn't present.
function deriveFilename(
  location: string | null,
  evalSetId: string | null
): string {
  if (!location || !evalSetId) return location ?? '';
  const parts = location.split(`${evalSetId}/`);
  return parts.length > 1 ? parts[parts.length - 1] : location;
}

// Map an event/message hit to the viewer's deep-link anchor (tab + search
// param). Other hit types have no addressable transcript node.
function viewAnchor(r: SearchResult): SampleViewAnchor | undefined {
  if (!r.transcript_anchor) return undefined;
  if (r.entity_type === 'event') {
    return { tab: 'transcript', param: 'event', value: r.transcript_anchor };
  }
  if (r.entity_type === 'message' || r.entity_type === 'message_pool') {
    return { tab: 'messages', param: 'message', value: r.transcript_anchor };
  }
  return undefined;
}

function resultTarget(r: SearchResult): ViewerTarget | null {
  if (!r.eval_set_id) return null;
  const logFile = r.location ? deriveFilename(r.location, r.eval_set_id) : '';
  if (r.sample_id && r.epoch != null && logFile) {
    return {
      level: 'sample',
      evalSetId: r.eval_set_id,
      logFile,
      sampleId: r.sample_id,
      epoch: r.epoch,
      sampleUuid: r.sample_uuid,
      anchor: viewAnchor(r),
    };
  }
  if (logFile) {
    return { level: 'log', evalSetId: r.eval_set_id, logFile };
  }
  return { level: 'eval_set', evalSetId: r.eval_set_id };
}

const CONTENT_KIND_LABELS: Record<ContentKind, string> = {
  transcript: 'transcript',
  scanned_transcript: 'scanned transcript',
  result_value: 'scan result',
  eval_metadata: 'eval',
  sample_metadata: 'sample',
};

function Badge({ result }: { result: SearchResult }) {
  const label = CONTENT_KIND_LABELS[result.content_kind] ?? result.content_kind;
  return (
    <span className="inline-flex self-start items-center rounded bg-surface-2 px-2 py-0.5 text-xs font-medium text-muted">
      {result.entity_type}
      {label && label !== result.entity_type ? ` · ${label}` : ''}
    </span>
  );
}

interface TranscriptGroup {
  key: string;
  evalSetId: string | null;
  taskName: string | null;
  model: string | null;
  sampleId: string | null;
  hits: SearchResult[];
}

function groupByTranscript(items: SearchResult[]): TranscriptGroup[] {
  const groups = new Map<string, TranscriptGroup>();
  for (const item of items) {
    const key = item.sample_pk ?? `eval:${item.eval_id ?? item.entity_pk}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        evalSetId: item.eval_set_id ?? null,
        taskName: item.task_name ?? null,
        model: item.model ?? null,
        sampleId: item.sample_id ?? null,
        hits: [],
      };
      groups.set(key, group);
    }
    group.hits.push(item);
  }
  return [...groups.values()];
}

// State that mirrors a URL param: locally editable, but re-synced when the param
// changes externally (back/forward nav). Adjusting state during render is
// React's "you might not need an effect" pattern — returning the same `last`
// value bails the update, so it can't loop with the debounce effect that writes
// the param.
function useUrlSyncedState(value: string) {
  const [state, setState] = useState(value);
  const [last, setLast] = useState(value);
  if (value !== last) {
    setLast(value);
    setState(value);
  }
  return [state, setState] as const;
}

export function TranscriptSearchList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { apiFetch } = useApiFetch();

  // Both follow their URL param on external nav (back/forward) while staying
  // locally editable — see useUrlSyncedState.
  const [query, setQuery] = useUrlSyncedState(searchParams.get('q') ?? '');
  const [evalSetId, setEvalSetId] = useUrlSyncedState(
    searchParams.get('eval_set') ?? ''
  );
  // Sort defaults to rank (relevance). The string round-trip through useUrlSyncedState
  // is fine — only "recency" needs a URL entry, anything else collapses to default.
  const [sortRaw, setSortRaw] = useUrlSyncedState(
    searchParams.get('sort') ?? DEFAULT_SORT
  );
  const sort: SearchSort = sortRaw === 'recency' ? 'recency' : DEFAULT_SORT;
  const [modeRaw, setModeRaw] = useUrlSyncedState(
    searchParams.get('mode') ?? DEFAULT_MODE
  );
  const mode: SearchMode = modeRaw === 'grep' ? 'grep' : DEFAULT_MODE;
  const [matchCaseRaw, setMatchCaseRaw] = useUrlSyncedState(
    searchParams.get('match_case') ?? 'false'
  );
  const matchCase = matchCaseRaw === 'true';

  // Key the whole result state to the current inputs so edits immediately hide
  // previous hits/cursors, including during the debounce before the next fetch.
  const searchKey = JSON.stringify([query, evalSetId, sort, mode, matchCase]);
  const [searchState, setSearchState] = useState<SearchState>(EMPTY_SEARCH);
  const {
    results,
    nextCursor,
    searchedThrough,
    indexStatus,
    method,
    tooBroad,
    recovery,
    loading,
    error,
    searched,
  } = searchState.key === searchKey ? searchState : EMPTY_SEARCH;

  // "Keep searching" applies to the search it was turned on for; `since`
  // starts the AUTO_SEARCH_MS clock.
  const [autoSearch, setAutoSearch] = useState<{
    key: string;
    since: number;
    stopped: 'time' | 'error' | null;
  } | null>(null);
  const autoOn = autoSearch?.key === searchKey;
  const keepSearching = autoOn && !autoSearch.stopped;
  const autoStopped = autoOn ? autoSearch.stopped : null;
  const startAutoSearch = () =>
    setAutoSearch({ key: searchKey, since: Date.now(), stopped: null });

  // Cleared once an API rejects `time_limited` for full-text and accepts the
  // same request without it.
  const fullTextTimeLimited = useRef(true);

  // Keep the latest in-flight request id so stale responses are ignored.
  const requestId = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);
  const cancelSearch = useCallback(() => {
    requestId.current++;
    activeRequest.current?.abort();
  }, []);

  const runSearch = useCallback(
    async ({
      query,
      evalSetId,
      sort,
      mode,
      matchCase,
      key,
      cursor = null,
      scanCursor = false,
    }: SearchRequest) => {
      // Whitespace is meaningful in an exact substring query.
      const searchQuery = mode === 'grep' ? query : query.trim();
      const id = ++requestId.current;
      activeRequest.current?.abort();
      if (!searchQuery) {
        setSearchState({ ...EMPTY_SEARCH, key });
        return;
      }
      const controller = new AbortController();
      activeRequest.current = controller;
      setSearchState(previous => ({
        ...(cursor && previous.key === key ? previous : EMPTY_SEARCH),
        key,
        loading: true,
        error: null,
      }));
      let recovery: SearchState['recovery'] = cursor ? null : 'retry';
      try {
        const params = new URLSearchParams({
          q: searchQuery,
          limit: String(LIMIT),
        });
        if (evalSetId) params.set('eval_set_id', evalSetId);
        if (mode === 'grep') {
          params.set('mode', mode);
          params.set('match_case', String(matchCase));
        } else if (sort !== DEFAULT_SORT) {
          params.set('sort', sort);
        }
        // Full-text scans instead of failing when the index times out.
        const timeLimited = mode === 'grep' || fullTextTimeLimited.current;
        if (timeLimited) params.set('time_limited', 'true');
        if (cursor) params.set('cursor', cursor);
        // returnNonOkResponse so a 4xx/5xx comes back as a response (and we can
        // surface it) instead of useApiFetch swallowing it to null.
        const fetchPage = () =>
          apiFetch(
            `/meta/search?${params.toString()}`,
            { signal: controller.signal },
            {
              returnNonOkResponse: true,
            }
          );
        let res = await fetchPage();
        if (id !== requestId.current) return; // superseded
        // APIs without full-text scans reject `time_limited` for full-text with
        // a 400. They cannot read a scan cursor, so that page restarts instead.
        if (
          mode === 'fts' &&
          timeLimited &&
          !(cursor && scanCursor) &&
          res?.status === 400 &&
          (await rejectsTimeLimited(res))
        ) {
          if (id !== requestId.current) return;
          params.delete('time_limited');
          res = await fetchPage();
          if (id !== requestId.current) return;
          if (res?.ok) fullTextTimeLimited.current = false;
        }
        if (!res) {
          throw new Error('Search request failed');
        }
        if (!res.ok) {
          // Rejected cursors need a fresh search; initial requests can retry temporary errors.
          recovery =
            cursor && res.status === 400
              ? 'restart'
              : !cursor && (res.status === 429 || res.status >= 500)
                ? 'retry'
                : null;
          // Surface the API's `detail` when present — it carries the actionable
          // hint for the 400 "too broad" and 504 "took too long" responses.
          // Fall back to the status line if the body isn't the expected shape.
          let message = `Search failed: ${res.status} ${res.statusText}`;
          try {
            const body: unknown = await res.json();
            const detail =
              body && typeof body === 'object' && 'detail' in body
                ? (body as { detail?: unknown }).detail
                : undefined;
            if (typeof detail === 'string' && detail) message = detail;
          } catch {
            // Non-JSON body — keep the status-line fallback.
          }
          throw new Error(message);
        }
        const data = (await res.json()) as SearchResponse;
        if (id !== requestId.current) return;
        // A page that returns its own cursor makes no progress.
        if (cursor && data.next_cursor === cursor) setAutoSearch(null);
        setSearchState(previous => ({
          key,
          // A too-broad page holds the newest matches, a list of its own.
          results:
            cursor && previous.key === key && !data.too_broad
              ? [...previous.results, ...data.items]
              : data.items,
          nextCursor: data.next_cursor ?? null,
          searchedThrough:
            data.searched_through ??
            (cursor && previous.key === key ? previous.searchedThrough : null),
          indexStatus: data.index_status ?? null,
          method: data.method ?? null,
          tooBroad: data.too_broad ?? null,
          recovery: null,
          searched: true,
          loading: false,
          error: null,
        }));
      } catch (err) {
        if (id === requestId.current) {
          setAutoSearch(auto =>
            auto && !auto.stopped ? { ...auto, stopped: 'error' } : null
          );
          setSearchState(previous => ({
            ...previous,
            nextCursor: recovery === 'restart' ? null : previous.nextCursor,
            recovery,
            error: err as Error,
          }));
        }
      } finally {
        if (id === requestId.current) {
          setSearchState(previous => ({ ...previous, loading: false }));
        }
      }
    },
    [apiFetch]
  );

  // Debounce inputs -> URL + search; cancel immediately when inputs change.
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      // Write the raw text: the input follows q, so trimming here would strip a
      // space the user just typed. runSearch trims the request itself.
      if (mode === 'grep' ? query : query.trim()) next.set('q', query);
      else next.delete('q');
      const trimmedEvalSet = evalSetId.trim();
      if (trimmedEvalSet) next.set('eval_set', trimmedEvalSet);
      else next.delete('eval_set');
      if (sort !== DEFAULT_SORT) next.set('sort', sort);
      else next.delete('sort');
      if (mode !== DEFAULT_MODE) next.set('mode', mode);
      else next.delete('mode');
      if (matchCase) next.set('match_case', 'true');
      else next.delete('match_case');
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
      void runSearch({
        query,
        evalSetId: trimmedEvalSet,
        sort,
        mode,
        matchCase,
        key: searchKey,
      });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
      cancelSearch();
      // "Keep searching" belongs to one search; returning to it later starts off.
      setAutoSearch(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    evalSetId,
    sort,
    mode,
    matchCase,
    searchKey,
    runSearch,
    cancelSearch,
  ]);

  const loadMore = () =>
    void runSearch({
      query,
      evalSetId: evalSetId.trim(),
      sort,
      mode,
      matchCase,
      key: searchKey,
      cursor: nextCursor,
      scanCursor: method === 'scan',
    });

  // Request the next page after each response while "Keep searching" is on.
  // runSearch marks the state loading, so only one request runs at a time.
  useEffect(() => {
    if (!keepSearching || !nextCursor || loading || error) return;
    if (Date.now() - autoSearch.since >= AUTO_SEARCH_MS) {
      setAutoSearch({ ...autoSearch, stopped: 'time' });
      return;
    }
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keepSearching, nextCursor, loading, error]);

  const groups = useMemo(() => groupByTranscript(results), [results]);

  const matchCount = searched
    ? `${results.length} match${results.length === 1 ? '' : 'es'}` +
      (nextCursor
        ? searchedThrough
          ? ` · searched back to ${formatTime(searchedThrough)}`
          : ' · more to search'
        : '')
    : '';
  const notices = [
    mode === 'fts' && method === 'scan' ? SCAN_NOTICE : null,
    tooBroad ? tooBroadNotice(tooBroad, results.length) : null,
    indexStatus && !indexStatus.complete ? indexNotice(indexStatus) : null,
  ].filter(Boolean);
  const autoStopMessage =
    autoStopped === 'time' && nextCursor
      ? 'Automatic search stopped after 10 minutes.'
      : autoStopped === 'error' && error
        ? 'Automatic search stopped after an error.'
        : null;

  return (
    <div className="pane flex h-full flex-col overflow-hidden">
      <PageHeader
        title="Transcript Search"
        description="Full-text search across every imported transcript and result. Scope it to one eval set to narrow."
        actions={
          <span className="pane-meta" role="status">
            {matchCount}
            {loading && (
              <span className="loading-placeholder ml-2">Searching…</span>
            )}
          </span>
        }
      >
        <div className="relative min-w-60 max-w-2xl flex-1">
          <input
            type="text"
            aria-label="Search transcripts"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search transcripts…"
            className="input h-7 w-full"
          />
        </div>
      </PageHeader>
      <div className="subbar">
        <div className="flex items-center gap-1.5">
          <label htmlFor="transcript-search-mode" className="subbar-label">
            Search type
          </label>
          <select
            id="transcript-search-mode"
            value={mode}
            onChange={e => setModeRaw(e.target.value)}
            className="input h-6 px-1.5 text-xs"
          >
            <option value="fts">Full-text</option>
            <option value="grep">Literal text</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="subbar-label">Eval set</span>
          <EvalSetFilter value={evalSetId} onChange={setEvalSetId} />
        </div>
        {mode === 'grep' ? (
          <div className="flex items-center gap-1.5">
            <input
              id="transcript-search-match-case"
              type="checkbox"
              checked={matchCase}
              onChange={e => setMatchCaseRaw(String(e.target.checked))}
              className="accent-primary"
            />
            <label
              htmlFor="transcript-search-match-case"
              className="text-xs text-ink-2"
            >
              Match case
            </label>
          </div>
        ) : (
          <div
            role="group"
            aria-label="Sort order"
            className="flex items-center gap-1.5"
          >
            <span className="subbar-label">Sort</span>
            <SortToggle value={sort} onChange={setSortRaw} />
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <input
            id="transcript-search-keep-searching"
            type="checkbox"
            checked={keepSearching}
            // A page error ends automatic paging until a request succeeds.
            disabled={Boolean(error)}
            onChange={e =>
              e.target.checked ? startAutoSearch() : setAutoSearch(null)
            }
            className="accent-primary"
          />
          <label
            htmlFor="transcript-search-keep-searching"
            className="text-xs text-ink-2"
          >
            Keep searching
          </label>
        </div>
        <SyntaxHint
          mode={mode}
          matchCase={matchCase}
          className="ml-auto hidden lg:block"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {/* A failed page keeps the pages already shown; the button below retries. */}
        {error && (
          <div role="alert" className="callout callout-err m-3">
            {error.message}
          </div>
        )}
        {notices.length > 0 && (
          <div role="status" className="callout callout-warn m-3">
            {notices.join(' ')}
          </div>
        )}
        {!error && !searched && !loading ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
            <p className="text-sm text-muted">
              Search every imported transcript and result. Pick an eval set to
              narrow.
            </p>
            {/* The toolbar carries the hint from lg up. */}
            <SyntaxHint
              mode={mode}
              matchCase={matchCase}
              className="lg:hidden"
            />
          </div>
        ) : !error &&
          searched &&
          results.length === 0 &&
          !loading &&
          !tooBroad ? (
          <p className="p-8 text-center text-sm text-muted">
            {nextCursor
              ? searchedThrough
                ? `No matches since ${formatTime(searchedThrough)}. Continue searching older transcripts.`
                : 'No matches yet. Continue searching older transcripts.'
              : evalSetId
                ? `No matches in eval set ${evalSetId}.`
                : 'No matches found.'}
          </p>
        ) : (
          <div>
            {groups.map(group => (
              <TranscriptGroup key={group.key} group={group} />
            ))}
          </div>
        )}
        {(nextCursor || recovery) && (
          <div className="flex flex-wrap items-center justify-center gap-2 p-4">
            {autoStopMessage && (
              <span role="status" className="text-sm text-muted">
                {autoStopMessage}
              </span>
            )}
            {autoStopped === 'time' && nextCursor && (
              <button
                type="button"
                disabled={loading}
                onClick={startAutoSearch}
                className="btn btn-ghost"
              >
                Resume automatic search
              </button>
            )}
            <button
              type="button"
              disabled={loading}
              onClick={loadMore}
              className="btn btn-ghost"
            >
              {loading
                ? 'Searching…'
                : recovery
                  ? recovery === 'restart'
                    ? 'Restart search'
                    : 'Retry search'
                  : results.length === 0
                    ? 'Continue searching'
                    : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// The 400 detail of an API that predates full-text scans.
const TIME_LIMITED_REJECTION =
  'Case and batch options are only supported for literal search.';

async function rejectsTimeLimited(res: Response): Promise<boolean> {
  try {
    const body: unknown = await res.clone().json();
    return (
      typeof body === 'object' &&
      body !== null &&
      'detail' in body &&
      body.detail === TIME_LIMITED_REJECTION
    );
  } catch {
    return false;
  }
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

// Plain words for the tables the API reports as not fully indexed.
const TABLE_WORDS: Record<string, string> = {
  eval: 'evals',
  sample: 'samples',
  message: 'messages',
  message_pool: 'messages',
  event: 'events',
  score: 'scores',
  scanner_result: 'scan results',
  sample_attachment: 'attachments',
};

function indexNotice(status: IndexStatus): string {
  const words = [
    ...new Set(status.incomplete_tables.map(t => TABLE_WORDS[t] ?? t)),
  ];
  if (words.length === 0) return 'The search index may be incomplete.';
  const list =
    words.length === 1
      ? words[0]
      : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return `The search index may be incomplete: ${list} are still being reindexed.`;
}

// "About 2.3 million matches, too many to rank. Showing the newest 50. Add
// another word, quote a phrase, or scope to an eval set for ranked results."
function tooBroadNotice(notice: TooBroad, shown: number): string {
  const matches = `About ${formatApproximateCount(notice.estimated_matches)} matches, too many to rank.`;
  const showing =
    shown > 0
      ? `Showing the newest ${shown.toLocaleString()}.`
      : 'Even the newest matches could not be fetched within the time budget.';
  const [first, ...rest] = notice.suggestions;
  if (!first) return `${matches} ${showing}`;
  const head = first.charAt(0).toUpperCase() + first.slice(1);
  const last = rest[rest.length - 1];
  const list =
    rest.length === 0
      ? head
      : rest.length === 1
        ? `${head} or ${last}`
        : `${[head, ...rest.slice(0, -1)].join(', ')}, or ${last}`;
  return `${matches} ${showing} ${list} for ranked results.`;
}

function SyntaxHint({
  mode,
  matchCase,
  className = '',
}: {
  mode: SearchMode;
  matchCase: boolean;
  className?: string;
}) {
  if (mode === 'grep') {
    return (
      <p className={`text-xs text-muted ${className}`}>
        Exact substring, {matchCase ? 'matching' : 'ignoring'} case · newest
        first · attachments are not searched
      </p>
    );
  }
  return (
    <p className={`text-xs text-muted ${className}`}>
      <code className="text-ink-2">{'"quoted"'}</code> matches those words in
      order · <code className="text-ink-2">OR</code> for either term ·{' '}
      <code className="text-ink-2">-word</code> to exclude
    </p>
  );
}

/** One transcript's hits: a surface-2 strip naming it, then its snippets. */
function TranscriptGroup({ group }: { group: TranscriptGroup }) {
  const metadata = [group.model, group.evalSetId].filter(Boolean).join(' · ');
  const { primaryUrl } = useViewers();
  const hitTarget = resultTarget(group.hits[0]);
  const groupTarget =
    hitTarget?.level === 'sample'
      ? { ...hitTarget, anchor: undefined }
      : hitTarget;

  return (
    <section className="border-b border-line">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-line bg-surface-2 px-4 py-2">
        <span className="break-all text-sm font-medium text-ink">
          {group.taskName || 'Result'}
        </span>
        {group.sampleId ? (
          <span className="text-xs text-muted">
            sample{' '}
            <span className="font-mono text-ink-2">{group.sampleId}</span>
          </span>
        ) : null}
        {metadata && (
          <span className="min-w-0 break-words text-xs text-muted">
            {metadata}
          </span>
        )}
        {groupTarget && (
          <OpenInViewerMenu
            target={groupTarget}
            subject={group.sampleId ?? group.evalSetId ?? undefined}
            className="ml-auto self-center"
          />
        )}
      </div>
      <ul className="divide-y divide-line">
        {group.hits.map(hit => {
          const target = resultTarget(hit);
          const href = target ? primaryUrl(target) : null;
          const body = (
            <div className="flex flex-col gap-1 px-4 py-2.5">
              <Badge result={hit} />
              <SearchSnippet snippet={hit.snippet ?? null} />
            </div>
          );
          return (
            <li key={`${hit.entity_type}:${hit.entity_pk}`}>
              {href ? (
                // Plain <a> (full reload) so the log-viewer's separate
                // stylesheet bundle loads cleanly — matches cross-viewer links.
                <a
                  href={href}
                  {...viewerLinkTarget(href)}
                  className="block no-underline hover:bg-hover"
                >
                  {body}
                </a>
              ) : (
                body
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

interface SortToggleProps {
  value: SearchSort;
  onChange: (next: SearchSort) => void;
}

/**
 * Two-button toggle for sort order. Relevance is the default; switch to
 * Newest for a strict newest-first view of the same matches.
 */
function SortToggle({ value, onChange }: SortToggleProps) {
  const options: { key: SearchSort; label: string; title: string }[] = [
    {
      key: 'rank',
      label: 'Relevance',
      title: 'Sort by relevance, newest first among equals',
    },
    { key: 'recency', label: 'Newest', title: 'Sort by newest first' },
  ];
  return (
    <div className="seg">
      {options.map(opt => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            aria-pressed={active}
            title={opt.title}
            onClick={() => onChange(opt.key)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
